import MuiStack from "@mui/material/Stack";
import { tokens } from "../tokens";
import { cleanProps } from "../internal/cleanProps";
import type { StackH2Gap, StackProps } from "../types";

const JUSTIFY: Record<NonNullable<StackProps["justify"]>, string> = {
  start: "flex-start",
  center: "center",
  end: "flex-end",
  "space-between": "space-between",
};

const ALIGN: Record<NonNullable<StackProps["align"]>, string> = {
  start: "flex-start",
  center: "center",
  end: "flex-end",
  stretch: "stretch",
};

/**
 * Containment note (types.ts): the upstream `RootComponent` generic is
 * fixed to `'div'` (no `as`/`component` on this type); `AdditionalProps`
 * is fixed to `{}`. `gap` forwards Material UI's spacing scale only
 * indirectly, through the closed `SpacingToken` union.
 */
const H2_GAP_PX: Record<StackH2Gap, number> = { field: 20, fieldTight: 18, heading: 6 };

function gapPx(gap: NonNullable<StackProps["gap"]>): number {
  return gap in H2_GAP_PX ? H2_GAP_PX[gap as StackH2Gap] : tokens.spacing[gap as keyof typeof tokens.spacing];
}

export function Stack({ direction = "column", gap, align, justify, wrap, id, children, testId }: Readonly<StackProps>) {
  return (
    <MuiStack
      direction={direction === "actions" || direction === "sectionActions" ? { xs: "column", sm: "row" } : direction === "row" || direction === "dialogActions" ? "row" : "column"}
      // ADR-20261102-h2fd: flex `gap`, as every H2 column uses, so a child's own margin (H2's
      // 24px under each form field) survives instead of being reset by Stack's margin spacing.
      useFlexGap
      {...cleanProps({
        spacing: gap === undefined ? undefined : gapPx(gap) / 4,
        alignItems:
          align !== undefined
            ? ALIGN[align]
            : direction === "actions" || direction === "sectionActions"
              ? { xs: "stretch", sm: "center" }
              : undefined,
        justifyContent: justify !== undefined ? JUSTIFY[justify] : undefined,
        flexWrap: wrap === true || direction === "dialogActions" ? ("wrap" as const) : undefined,
        // H2: the dialog and section action rows sit 8px below what precedes them.
        sx: direction === "dialogActions" || direction === "sectionActions" ? { paddingTop: `${tokens.spacing.xs}px` } : undefined,
        id,
        "data-testid": testId,
      })}
    >
      {children}
    </MuiStack>
  );
}
